/**
 * Platform SaaS public legal chrome — `/legal/*` under AdminLoginShell.
 * Document bodies load from GET `/api/v1/public/legal-documents/:purpose`.
 * Expanded purposes coordinate with BE LEGAL_DOCUMENT_PURPOSES (sibling pass).
 */

export const PLATFORM_LEGAL_SLUGS = [
  'terms',
  'privacy',
  'returns',
  'withdrawal',
  'recurring-payments',
  'payments',
  'imprint',
  'refund-support',
] as const;

export type PlatformLegalSlug = (typeof PLATFORM_LEGAL_SLUGS)[number];

/** CMS purpose codes for sandboxed iframe document routes. */
export type PlatformLegalDocumentPurpose =
  | 'TERMS_OF_SERVICE'
  | 'PRIVACY_NOTICE'
  | 'RETURNS_AND_COMPLAINTS'
  | 'WITHDRAWAL_RIGHTS'
  | 'RECURRING_PAYMENT_TERMS'
  | 'PAYMENT_METHODS';

export interface PlatformLegalRouteDef {
  readonly slug: PlatformLegalSlug;
  readonly path: `/legal/${PlatformLegalSlug}`;
  /** null → imprint / operator chrome (not a LegalDocument purpose). */
  readonly purpose: PlatformLegalDocumentPurpose | null;
  readonly titleKey: string;
  readonly footerKey: string;
}

export const PLATFORM_LEGAL_ROUTES: readonly PlatformLegalRouteDef[] = [
  {
    slug: 'terms',
    path: '/legal/terms',
    purpose: 'TERMS_OF_SERVICE',
    titleKey: 'legal.pages.terms.title',
    footerKey: 'legal.footer.terms',
  },
  {
    slug: 'privacy',
    path: '/legal/privacy',
    purpose: 'PRIVACY_NOTICE',
    titleKey: 'legal.pages.privacy.title',
    footerKey: 'legal.footer.privacy',
  },
  {
    slug: 'returns',
    path: '/legal/returns',
    purpose: 'RETURNS_AND_COMPLAINTS',
    titleKey: 'legal.pages.returns.title',
    footerKey: 'legal.footer.returns',
  },
  {
    slug: 'withdrawal',
    path: '/legal/withdrawal',
    purpose: 'WITHDRAWAL_RIGHTS',
    titleKey: 'legal.pages.withdrawal.title',
    footerKey: 'legal.footer.withdrawal',
  },
  {
    slug: 'recurring-payments',
    path: '/legal/recurring-payments',
    purpose: 'RECURRING_PAYMENT_TERMS',
    titleKey: 'legal.pages.recurringPayments.title',
    footerKey: 'legal.footer.recurringPayments',
  },
  {
    slug: 'payments',
    path: '/legal/payments',
    purpose: 'PAYMENT_METHODS',
    titleKey: 'legal.pages.payments.title',
    footerKey: 'legal.footer.payments',
  },
  {
    slug: 'imprint',
    path: '/legal/imprint',
    purpose: null,
    titleKey: 'legal.pages.imprint.title',
    footerKey: 'legal.footer.imprint',
  },
  {
    slug: 'refund-support',
    path: '/legal/refund-support',
    purpose: 'RETURNS_AND_COMPLAINTS',
    titleKey: 'legal.pages.refundSupport.title',
    footerKey: 'legal.footer.refundSupport',
  },
] as const;

export function isPlatformLegalSlug(value: string): value is PlatformLegalSlug {
  return (PLATFORM_LEGAL_SLUGS as readonly string[]).includes(value);
}

export function findPlatformLegalRoute(slug: string): PlatformLegalRouteDef | null {
  if (!isPlatformLegalSlug(slug)) {
    return null;
  }
  return PLATFORM_LEGAL_ROUTES.find((route) => route.slug === slug) ?? null;
}

/** Footer strip on AdminLoginShell — subset always visible. */
export const PLATFORM_LEGAL_FOOTER_SLUGS: readonly PlatformLegalSlug[] = [
  'terms',
  'privacy',
  'recurring-payments',
  'returns',
  'withdrawal',
  'imprint',
  'payments',
  'refund-support',
];
